import {ellipsis} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {FrontEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ConversationSummaryFactModel} from '../../../../../../../libs/shared/core/src/models/api/factModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {getIsUserGlobalAdmin} from '../../../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {TooltipShortcutFormatter} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {SettingsRootPagesEnum} from '../../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../../models/url/settingsUrlHelpers';
import {CopyButton} from '../../../../../common/buttons/copyButton';
import {ConversationSummaryAiSummarize} from '../../conversationSummary/conversationSummaryAiSummarize';
import {ConversationHeaderAiSummarizeWrapper} from './conversationHeaderAiSummarizeWrapper';

/*
 * Props.
 */

interface ConversationHeaderAiSummarizeButtonProps {
  conversation: ConversationModel | undefined;
  summary: ConversationSummaryFactModel;
  shouldExpandSummary: boolean;
  teammate: TeammateModel;
}

/*
 * Style.
 */

const AiSummarizeTitleSpan = styled.span<{$isExpanded: boolean}>`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  grid-area: text;
  margin: 4px 0px 4px 8px;
  line-height: 18px;
  ${(p) => (!p.$isExpanded ? ellipsis() : null)}
`;

const StyledLeftIcon = styled(Icon)`
  grid-area: left;
  height: 16px;
  margin-top: 5px;
`;

const StyledRightIconsDiv = styled.div`
  position: relative;
  grid-area: right;
  display: flex;
  flex-direction: row-reverse;
`;

const StyledActionItemsDiv = styled.div`
  position: absolute;
  right: 20px;
  display: none;
  flex-direction: row-reverse;
  background: ${(p) => p.theme.greys.shade30};

  ${hoverSelector} {
    display: flex;
  }
  &::before {
    position: absolute;
    left: -8px;
    background: linear-gradient(to left, ${(p) => p.theme.greys.shade30}, transparent 90%);
    content: '';
    width: 8px;
    height: 100%;
  }
`;

const StyledExpandIcon = styled(Icon)`
  height: 26px;
`;

/*
 * Component.
 */

export const ConversationHeaderAiSummarizeButton: FC<ConversationHeaderAiSummarizeButtonProps> = (props) => {
  const {summary, shouldExpandSummary, teammate} = props;
  const [isOverActions, setIsOverActions] = useState(false);
  const theme = useUserTheme();
  const [isConversationSummaryOpen, setIsConversationSummaryOpen] = useState(shouldExpandSummary);
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();
  const trackingBaseBlob = {
    teammate_id: teammate.id,
    fact_id: summary.id,
  };
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  useInitialEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, ViewerDataEventObject.CONVERSATION_SUMMARY, {
      ...trackingBaseBlob,
    });
    if (isConversationSummaryOpen) {
      trackDataEvent(DataEventActionsEnum.EXPAND, ViewerDataEventObject.CONVERSATION_SUMMARY, {
        source: 'automatic',
        ...trackingBaseBlob,
      });
    }
  });

  /*
   * Event Handlers.
   */

  const onSummarizeAreaClick = (event: FrontEvent) => {
    // Don't toggle if we clicked the copy button or settings and this is the event bubbling up
    if (event.defaultPrevented) {
      return;
    }

    const action = isConversationSummaryOpen ? DataEventActionsEnum.COLLAPSE : DataEventActionsEnum.EXPAND;
    trackDataEvent(action, ViewerDataEventObject.CONVERSATION_SUMMARY, {
      source: 'manual',
      ...trackingBaseBlob,
    });

    setIsConversationSummaryOpen(!isConversationSummaryOpen);
  };

  const onSettingsClick = (event: FrontEvent) => {
    // prevent the default so we don't toggle the summary area
    event.preventDefault();

    trackDataEvent(
      DataEventActionsEnum.CLICK,
      ViewerDataEventObject.CONVERSATION_SUMMARY_SETTINGS,
      trackingBaseBlob,
    );

    navigateToLink(buildSettingsPath({rootPage: SettingsRootPagesEnum.AI}));
  };

  /*
   * Render.
   */

  const renderSummaryTopLine = () => (
    <>
      <StyledLeftIcon name="sparklesAiFilledSmall" />
      <AiSummarizeTitleSpan $isExpanded={isConversationSummaryOpen}>{summary.oneliner}</AiSummarizeTitleSpan>
    </>
  );

  const maybeRenderSettingsButton = () => {
    // Global admins are allowed to decide where auto-summarize is deployed.
    if (isGlobalAdmin) {
      return (
        <IconButton
          iconName="gearSmall"
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          label={<Trans id="conversation-header-ai-summarize-go-to-settings">Go to settings</Trans>}
          onClick={(event) => onSettingsClick(event)}
        />
      );
    }
    return null;
  };

  const onCopyClickTracking = () => {
    trackDataEvent(DataEventActionsEnum.COPY, ViewerDataEventObject.CONVERSATION_SUMMARY, trackingBaseBlob);
  };

  const onRenderTooltip = () => {
    if (isOverActions) {
      return undefined;
    }
    if (isConversationSummaryOpen) {
      return tooltipCollapseRenderer();
    }
    return tooltipExpandRenderer();
  };

  return (
    <ConversationHeaderAiSummarizeWrapper
      onKeyCommand={() => {
        const action = isConversationSummaryOpen
          ? DataEventActionsEnum.COLLAPSE
          : DataEventActionsEnum.EXPAND;
        trackDataEvent(action, ViewerDataEventObject.CONVERSATION_SUMMARY, {
          source: 'keyboard_shortcut',
          ...trackingBaseBlob,
        });

        setIsConversationSummaryOpen(!isConversationSummaryOpen);
      }}
      renderTooltip={() => onRenderTooltip()}
      onClick={onSummarizeAreaClick}
    >
      {isConversationSummaryOpen ? (
        <>
          {renderSummaryTopLine()}
          <StyledRightIconsDiv
            onMouseEnter={() => setIsOverActions(true)}
            onMouseLeave={() => setIsOverActions(false)}
          >
            <StyledExpandIcon name="chevronUpSmall" />
            <StyledActionItemsDiv>
              {maybeRenderSettingsButton()}
              <CopyButton
                preventDefault
                copyValue={generateSummaryCopyValue(summary)}
                onClickTracking={onCopyClickTracking}
              />
            </StyledActionItemsDiv>
          </StyledRightIconsDiv>
          <ConversationSummaryAiSummarize summary={summary} />
        </>
      ) : (
        <>
          {renderSummaryTopLine()}
          <StyledRightIconsDiv>
            <StyledExpandIcon name="chevronDownSmall" />
          </StyledRightIconsDiv>
        </>
      )}
    </ConversationHeaderAiSummarizeWrapper>
  );
};

/*
 * Helpers.
 */

function tooltipExpandRenderer(): React.ReactNode {
  return (
    <Trans id="conversation-header-ai-summarize-label-text">
      {'Expand summary '}
      <TooltipShortcutFormatter action={KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY} />
    </Trans>
  );
}

function tooltipCollapseRenderer(): React.ReactNode {
  return (
    <Trans id="conversation-header-ai-summarize-label-text">
      {'Collapse summary '}
      <TooltipShortcutFormatter action={KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY} />
    </Trans>
  );
}

function generateSummaryCopyValue(summary: ConversationSummaryFactModel) {
  return `${summary.oneliner}\n${summary.summary.map((item) => `- ${item}\n`).join('')}`;
}
